import { Box, Popper, SxProps } from "@outschool/backpack";
import { useTrackEvent } from "@outschool/ui-analytics";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Container } from "@outschool/ui-legacy-component-library";
import {
  useClickOutsideRefEventListener,
  useEventListener,
  useNavigation,
} from "@outschool/ui-utils";
import lodashDebounce from "lodash/debounce";
import React, { useCallback, useRef, useState } from "react";

import { useClpFilterPersistence } from "../../../lib/clpFilterPersistence";
import {
  CATEGORY_NAVBAR_COMPONENT,
  trackSubmenuOpen,
} from "./categoryNavbarTracking";
import { getCategoryUrl } from "./categoryNavbarUtils";
import { DesktopDropdown } from "./DesktopDropdown";
import { CategoryNavLink } from "./navbarCategories";

const NAVBAR_FALLBACK_PLACEMENTS = ["left", "right"] as const;

export default function DesktopCategoryNavbar({
  categories,
  sx = {},
}: {
  categories: CategoryNavLink[];
  sx?: SxProps;
}) {
  const navbarRef = useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const trackEvent = useTrackEvent();
  const document = (
    typeof window !== "undefined" ? window.document : null
  ) as Document | null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const select = useCallback(
    lodashDebounce((slug: string | undefined) => {
      if (slug && slug !== selectedCategory) {
        const category = categories.find(cat => cat.slug === slug);
        if (category) {
          trackSubmenuOpen(trackEvent, category, {
            wasAlreadySelected: false,
            component: CATEGORY_NAVBAR_COMPONENT.HORIZONTAL_NAVBAR,
          });
        }
      }
      setSelectedCategory(slug);
    }, 250),
    [setSelectedCategory, selectedCategory, categories, trackEvent],
  );
  const deselect = useCallback(() => select(undefined), [select]);

  useClickOutsideRefEventListener(document, navbarRef, deselect);

  const onEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        deselect();
      }
    },
    [deselect],
  );
  // @ts-ignore TS7
  useEventListener(document, "keyup", onEscape);

  const mouseListener = useCallback(
    (event: MouseEvent) => {
      const isOutside = [navbarRef].every(
        ref => !ref.current?.contains(event.target as HTMLElement),
      );
      if (isOutside) {
        deselect();
      }
    },
    [navbarRef, deselect],
  );
  // @ts-ignore TS7
  useEventListener(document, "mouseover", mouseListener);
  const renderSubcategoryCallback = React.useCallback(
    (category: CategoryNavLink) => {
      return () => <DesktopDropdown category={category} closeMenu={deselect} />;
    },
    [deselect],
  );

  return (
    <Box sx={sx}>
      <Container>
        <Box
          flex
          sx={{
            position: "relative",
            height: "48px",
            columnGap: "20px",
            justifyContent: "space-between",
            alignItems: "stretch",
          }}
          ref={navbarRef}
        >
          {categories.map((category: CategoryNavLink, index: number) => (
            <NavbarItem
              key={`${category.slug}:${
                category?.getSubcategories?.().length || 0
              }:${index}`}
              parentRef={navbarRef}
              uniqueId={category.slug}
              label={category.nameTitleCased}
              renderPopupContents={renderSubcategoryCallback(category)}
              isSelected={selectedCategory === category.slug}
              select={() => select(category.slug)}
              link={category.can_link ? getCategoryUrl(category) : undefined}
              reloadDocument={!!category.reloadDocument}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
}

function NavbarItem({
  uniqueId,
  label,
  renderPopupContents,
  parentRef,
  select,
  isSelected,
  link,
  reloadDocument,
}: {
  uniqueId: string;
  label: string | React.ReactElement;
  renderPopupContents: (isActive: boolean) => React.ReactNode;
  parentRef: React.RefObject<HTMLElement>;
  select: () => void;
  isSelected: boolean;
  link?: string;
  reloadDocument: boolean;
}) {
  const anchorRef = useRef<HTMLElement | null>(null);

  const navigate = useNavigation();
  const { getPathWithPersistedFilters, triggerPersistenceExperiment } =
    useClpFilterPersistence();
  const goToLink = useCallback(() => {
    if (link) {
      triggerPersistenceExperiment();
      navigate(getPathWithPersistedFilters(link), {
        hardNav: reloadDocument,
      });
    }
  }, [
    getPathWithPersistedFilters,
    link,
    navigate,
    reloadDocument,
    triggerPersistenceExperiment,
  ]);

  const handleClick = () => {
    select();
  };
  const shouldRenderPopover = isSelected && !!anchorRef.current;

  return (
    <>
      <NavbarButton
        isActive={isSelected}
        ref={anchorRef}
        onClick={handleClick}
        onMouseUp={goToLink}
        onMouseEnter={select}
        label={label}
        uniqueId={uniqueId}
      />
      {shouldRenderPopover && (
        <Popper
          open={shouldRenderPopover}
          anchorEl={anchorRef.current}
          placement="bottom"
          useArrow={false}
          disablePortal
          modifiers={[
            {
              name: "preventOverflow",
              options: {
                boundary: parentRef.current ?? undefined,
                fallbackPlacements: NAVBAR_FALLBACK_PLACEMENTS,
              },
            },
          ]}
          sx={{
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
            overflow: "visible",
            padding: 0,
          }}
        >
          <Box
            sx={{
              zIndex: 1000,
              maxWidth: "calc(100vw - 32px)",
              background: "white",
              border: "1px solid rgba(37, 93, 173, 0.25)",
              borderRadius: 5,
              boxShadow: "rgb(0 0 0 / 16%) 0px 8px 20px",
              overflow: "hidden",
            }}
          >
            {renderPopupContents(isSelected)}
          </Box>
        </Popper>
      )}
    </>
  );
}

type NavbarButtonProps = {
  label: string | React.ReactNode;
  uniqueId: string;
  onClick: () => void;
  onMouseEnter: (event: MouseEvent) => void;
  onMouseUp: (event: MouseEvent) => void;
  isActive: boolean;
};

const NavbarButton = React.forwardRef<HTMLElement, NavbarButtonProps>(
  ({ label, uniqueId, onClick, onMouseEnter, onMouseUp, isActive }, ref) => {
    return (
      <Box
        ref={ref}
        onMouseEnter={onMouseEnter}
        sx={{
          height: "48px",
          lineHeight: "48px",
          paddingTop: "2px",
          borderBottom: "2px solid rgba(0,0,0,0)",

          "& > button": {
            color: isActive ? "primary.700" : "grey.900",
            fontWeight: "fontWeightRegular",
          },

          borderColor: isActive ? "primary.700" : undefined,

          "&:hover": {
            borderColor: "primary.700",

            "& > button:not(:disabled)": {
              color: "primary.700",
              textDecoration: "none",
            },
          },

          "&:focus-within": {
            "& > button:not(:disabled)": {
              color: "primary.700",
              textDecoration: "none",
            },
          },
        }}
      >
        <TrackedButton
          variant="link"
          trackingName="category-navbar-desktop-item"
          trackingUniqueId={uniqueId}
          trackingSharedProperties={{
            category: uniqueId,
            component: CATEGORY_NAVBAR_COMPONENT.HORIZONTAL_NAVBAR,
          }}
          trackViews={true}
          onClick={onClick}
          onMouseUp={onMouseUp}
          sx={{
            whitSpace: "nowrap",
            height: "100%",
          }}
        >
          {label}
        </TrackedButton>
      </Box>
    );
  },
);
